import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { AlertTriangle, ArrowUpRight, Sparkles } from "lucide-react";

const FIELD_LABEL = {
  amount: "Balance",
  status: "Status",
  date_reported: "Date reported",
};

/**
 * Displays a "Guaranteed-win dispute" card for every tri-merge account where the bureaus
 * disagree on balance/status/date_reported. Under FCRA §1681e(b), inconsistent reporting is
 * per-se inaccurate.
 */
export function DiscrepancyFinder() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/negative-items/discrepancies");
        setGroups(data.groups || []);
      } catch (err) {
        // Silent — not everyone will have tri-merge data
        console.warn("discrepancy load", err?.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading || groups.length === 0) return null;

  return (
    <div
      className="card-surface p-6 mb-6 relative overflow-hidden border-[#FF6B4A]/30 bg-gradient-to-br from-[#FF6B4A]/5 to-transparent"
      data-testid="discrepancy-finder"
    >
      <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-[#FF6B4A]/10 blur-[100px] pointer-events-none" />
      <div className="relative">
        <div className="flex items-start gap-3 mb-5">
          <div className="w-10 h-10 rounded-sm bg-[#FF6B4A]/15 border border-[#FF6B4A]/30 flex items-center justify-center flex-shrink-0">
            <AlertTriangle size={16} className="text-[#FF6B4A]" />
          </div>
          <div>
            <div className="inline-flex items-center gap-2 border border-[#FF6B4A]/30 bg-[#FF6B4A]/5 rounded-full px-2.5 py-0.5 mb-2">
              <Sparkles size={10} className="text-[#FF6B4A]" />
              <span className="text-[10px] font-mono uppercase tracking-widest text-[#FF6B4A]">
                Guaranteed-win disputes · FCRA §1681e(b)
              </span>
            </div>
            <h3 className="font-display text-2xl md:text-3xl leading-tight">
              {groups.length} account{groups.length === 1 ? "" : "s"} reporting <span className="text-[#FF6B4A]">differently</span> across bureaus.
            </h3>
            <p className="text-sm text-[#A8ADA5] mt-1.5 max-w-xl">
              Inconsistent reporting between bureaus is per-se inaccurate under FCRA §1681e(b). These
              disputes get deleted at a much higher rate — draft &amp; mail them first.
            </p>
          </div>
        </div>

        <div className="space-y-3">
          {groups.slice(0, 5).map((g) => (
            <div key={g.item_group_id} className="rounded-sm border border-[#26282C] p-4 bg-[#0B0C0E]">
              <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
                <div>
                  <div className="font-medium text-[#F5F3EE]">
                    {g.creditor}
                    {g.account_number && (
                      <span className="text-xs text-[#6D716C] font-mono ml-2">
                        #{g.account_number}
                      </span>
                    )}
                  </div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-[#6D716C] mt-1">
                    {g.item_type?.replace("_", " ")}
                  </div>
                </div>
                <div className="flex gap-1.5 flex-wrap">
                  {g.discrepancy_fields.map((f) => (
                    <span
                      key={f}
                      className="text-[9px] px-2 py-0.5 rounded-full font-mono uppercase tracking-widest bg-[#FF6B4A]/15 text-[#FF6B4A]"
                    >
                      {FIELD_LABEL[f] || f} mismatch
                    </span>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 text-xs">
                {g.rows.map((r) => (
                  <div key={r.id} className="rounded-sm border border-[#26282C] p-2.5 bg-[#131518]">
                    <div className="overline mb-1">{r.bureau}</div>
                    <div className="ticker text-sm text-[#F5F3EE]">
                      {r.amount != null ? `$${Number(r.amount).toLocaleString()}` : "—"}
                    </div>
                    {r.bureau_status && (
                      <div className="text-[10px] text-[#A8ADA5] font-mono mt-0.5 truncate">
                        {r.bureau_status}
                      </div>
                    )}
                    {r.date_reported && (
                      <div className="text-[10px] text-[#6D716C] font-mono mt-0.5">
                        {r.date_reported}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-5 flex flex-wrap gap-2">
          <Link to="/app/items">
            <Button
              data-testid="discrepancy-finder-cta"
              className="rounded-full btn-lime h-10 px-5"
            >
              Draft these first <ArrowUpRight size={13} className="ml-1.5" />
            </Button>
          </Link>
          {groups.length > 5 && (
            <div className="flex items-center text-xs text-[#A8ADA5] font-mono ml-2">
              +{groups.length - 5} more with mismatches
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
